import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Input, Button, Tabs, Upload, Image, Select, message, Avatar } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import CommonFooter from "../../core/common/footer/commonFooter";
import { editUser } from "../../core/redux/user.slice";
import { changePassword } from "../../core/redux/user.slice";
import { photoService } from "../../core/services/photo.service";
import { getBase64 } from "../../core/utils/files.helper";
import { useTranslation } from "react-i18next";
import languageHelper from "../../utils/languageHelper";
const { TabPane } = Tabs;
const Profile = () => {
  const dispatch = useDispatch();
  const [personalForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { stores } = useSelector((state) => state.store);
  const { company } = useSelector((state) => state.company);

  // Load user data into form
  useEffect(() => {
    if (!user) return;

    personalForm.setFieldsValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      login: user.login,
      companyPosition: user.companyPosition,
      shopIds: user.shopIds || [],
      preferredLang:
        localStorage.getItem("app-language") ||
        user.preferredLang ||
        languageHelper.getCurrentLanguage(),
    });

    if (user.photos?.length > 0) {
      const principalPhoto = user.photos.find(
        (photo) => photo.isPrincipal
      );

      setPreviewImage(
        principalPhoto?.presignedUrl ||
        user.photos[0]?.presignedUrl
      );
    }
  }, [user, personalForm]);
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj || file);
    }
    setPreviewImage(file.url || file.preview);
  };

  const props = {
    onRemove: () => {
      setFile(null);
      setPreviewImage("");
    },
    beforeUpload: async (file) => {
      setFile(file);
      await handlePreview(file);
      return false;
    },
    fileList: [],
  };

  const handleUploadPhoto = async () => {
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', user.id);
      formData.append('isPrincipal', 'true');

      const uploadedPhoto = await photoService.uploadPhoto(formData);
      const photoWithPresignedUrl = await photoService.getPhotoWithPresignedUrl(uploadedPhoto);

      // Update preview with new photo
      setPreviewImage(photoWithPresignedUrl.presignedUrl);
      message.success(t("profile.photo.success"));
      setFile(null);
    } catch (error) {
      message.error(t("profile.photo.error"));
      console.error(t("profile.photo.error"), error);
    }
  };

  const handlePersonalInfoSubmit = async () => {
    try {
      setLoading(true);
      const values = await personalForm.validateFields();

      // Update backend
      await dispatch(editUser({
        id: user.id,
        user: {
          ...values,
          companyId: company?.companyId,
        }
      })).unwrap();

      if (values.preferredLang) {
        await languageHelper.changeLanguage(values.preferredLang);
      }

      // Upload photo if there's a new one
      if (file) {
        await handleUploadPhoto();
      }

      message.success(t("profile.messages.updateSuccess"));
    } catch (error) {
      message.error(t("profile.messages.updateError")); // ⭐ Fix typo
      console.error('Update failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      setLoading(true);
      const values = await passwordForm.validateFields();

      await dispatch(changePassword({
        id: user.id,
        passwords: values
      })).unwrap();

      message.success(t("profile.password.success"));
      passwordForm.resetFields();
    } catch (error) {
      message.error(t("profile.password.error"));
      console.error('Password change failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get user initials
  const getUserInitials = (firstName, lastName) => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${firstInitial}${lastInitial}`;
  };
  // Helper function to get user photo with fallback
  const userPhotoUrl =
    user?.photos?.find((photo) => photo.isPrincipal)?.presignedUrl ||
    user?.photos?.[0]?.presignedUrl ||
    null;
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>{t("profile.title")}</h4>
            <h6>{t("profile.subtitle")}</h6>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <Tabs defaultActiveKey="1">
              <TabPane
                tab={
                  <span>
                    <i className="ti ti-user me-2" />
                    {t("profile.tabs.personalInfo")}
                  </span>
                }
                key="1"
              >
                <Form form={personalForm} layout="vertical">
                  {/* Photo Upload Section */}
                  <div className="mb-4">
                    <Form.Item label={t("profile.photo.label")}>
                      <div className="d-flex align-items-center gap-3">
                        {/* Preview */}
                        <div>
                          {previewImage || userPhotoUrl ? (
                            <Image
                              src={previewImage || userPhotoUrl}
                              alt="Profile"
                              fallback="/assets/img/users/user-00.png"
                              style={{
                                width: 80,
                                height: 80,
                                borderRadius: "8px",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.currentTarget.src = "/assets/img/users/user-00.png";
                              }}
                            />
                          ) : (
                            <Avatar
                              size={80}
                              style={{
                                background: "var(--primary-gradient)",
                                fontSize: "14px",
                              }}
                            >
                              {getUserInitials(user?.firstName, user?.lastName)}
                            </Avatar>
                          )}
                        </div>

                        {/* Upload Button */}
                        <div>
                          <Upload {...props} showUploadList={false}>
                            <Button icon={<UploadOutlined />}>
                              {t("profile.photo.change")}
                            </Button>
                          </Upload>
                          <p className="text-muted mt-2 mb-0" style={{ fontSize: '12px' }}>
                            {t("profile.photo.hint")}
                          </p>
                        </div>
                      </div>
                    </Form.Item>
                  </div>

                  <div className="row">
                    <div className="col-lg-6">
                      <Form.Item
                        label={t("profile.form.firstName")}
                        name="firstName"
                        rules={[{ required: true, message: t("profile.validation.firstName") }]}
                      >
                        <Input placeholder={t("profile.form.firstName")} />
                      </Form.Item>
                    </div>

                    <div className="col-lg-6">
                      <Form.Item
                        label={t("profile.form.lastName")}
                        name="lastName"
                        rules={[{ required: true, message: t("profile.validation.lastName") }]}
                      >
                        <Input placeholder={t("profile.form.lastName")} />
                      </Form.Item>
                    </div>

                    <div className="col-lg-6">
                      <Form.Item
                        label={t("profile.form.email")}
                        name="email"
                        rules={[
                          { required: true, message: t("profile.validation.firstName") },
                          { type: 'email', message: t("profile.validation.emailRequired") }
                        ]}
                      >
                        <Input placeholder={t("profile.form.email")} />
                      </Form.Item>
                    </div>

                    <div className="col-lg-6">
                      <Form.Item
                        label={t("profile.form.phone")}
                        name="phone"
                        rules={[{ required: true, message: t("profile.validation.phone") }]}
                      >
                        <Input placeholder={t("profile.form.phone")} />
                      </Form.Item>
                    </div>

                    <div className="col-lg-6">
                      <Form.Item
                        label={t("profile.form.username")}
                        name="login"
                        rules={[{ required: true, message: t("profile.validation.username") }]}
                      >
                        <Input placeholder={t("profile.form.username")} />
                      </Form.Item>
                    </div>

                    <div className="col-lg-6">
                      <Form.Item
                        label={t("profile.form.position")}
                        name="companyPosition"
                      >
                        <Input placeholder={t("profile.form.position")} />
                      </Form.Item>
                    </div>

                    <div className="col-lg-6">
                      <Form.Item
                        label={t("profile.form.stores")}
                        name="shopIds"
                      >
                        <Select
                          mode="multiple"
                          placeholder={t("profile.form.stores")}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          options={stores.map(store => ({
                            value: store.shopId,
                            label: store.shopName
                          }))}
                        />
                      </Form.Item>
                    </div>
                    <div className="col-lg-6">
                      <Form.Item
                        label={t("profile.form.language")}
                        name="preferredLang"
                      >
                        <Select
                          label={t("profile.form.languagePlaceholder")}
                          options={[
                            { value: 'EN', label: 'English' },
                            { value: 'FR', label: 'Français' }
                          ]}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end">
                    <Button
                      type="primary"
                      onClick={handlePersonalInfoSubmit}
                      loading={loading}
                    >
                      {t("common.save")}
                    </Button>
                  </div>
                </Form>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="ti ti-lock me-2" />
                    {t("profile.password.change")}
                  </span>
                }
                key="2"
              >
                <Form form={passwordForm} layout="vertical">
                  <div className="row">
                    <div className="col-lg-12">
                      <Form.Item
                        name="oldPassword"
                        label={t("profile.password.oldPassword")}
                        rules={[{ required: true, message: t("profile.validation.oldPassword") }]}
                      >
                        <Input.Password placeholder={t("profile.password.oldPassword")} />
                      </Form.Item>
                    </div>

                    <div className="col-lg-6">
                      <Form.Item
                        name="newPassword"
                        label={t("profile.password.newPassword")}
                        rules={[
                          { required: true, message: t("profile.validation.newPassword") },
                          { min: 6, message: t("profile.validation.passwordMin") }
                        ]}
                      >
                        <Input.Password placeholder={t("profile.password.newPassword")} />
                      </Form.Item>
                    </div>

                    <div className="col-lg-6">
                      <Form.Item
                        name="confirmNewPassword"
                        label={t("profile.password.confirmPassword")}
                        dependencies={['newPassword']}
                        rules={[
                          { required: true, message: t("profile.validation.confirmPassword") },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('newPassword') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error(t("profile.validation.passwordMatch")));
                            },
                          }),
                        ]}
                      >
                        <Input.Password placeholder={t("profile.password.confirmPassword")} />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end">
                    <Button
                      type="primary"
                      onClick={handlePasswordChange}
                      loading={loading}
                    >
                      {t("profile.password.change")}
                    </Button>
                  </div>
                </Form>
              </TabPane>
            </Tabs>
          </div>
        </div>
      </div>
      <CommonFooter />
    </div>
  );
};

export default Profile;
